import React, { useEffect } from 'react';
import { SEOHead } from '../components/SEOHead';
import { ChallengeSection } from './sections/ChallengeSection';
import { MethodsSection } from './sections/MethodsSection';
import { IntelligenceSection } from './sections/IntelligenceSection';
import { DemoSection } from './sections/DemoSection';
import { DifferentSection } from './sections/DifferentSection';
import { Highlight } from '../patientintel/Highlight';
import { GSKGate } from './GSKGate';
import {
  palette,
  fonts,
  sectionInner,
  labelStyle,
  h1Style,
  h2Style,
  italicStyle,
  leadStyle,
} from './styles';

const useGoogleFonts = () => {
  useEffect(() => {
    if (document.getElementById('gsk-fonts')) return;
    const link = document.createElement('link');
    link.id = 'gsk-fonts';
    link.rel = 'stylesheet';
    link.href =
      'https://fonts.googleapis.com/css2?family=Source+Serif+Pro:ital,wght@0,400;0,600;0,700;1,400;1,600&family=DM+Mono:wght@400;500&display=swap';
    document.head.appendChild(link);
  }, []);
};

// Reveal animations are now driven by a CSS keyframe (see Styles below) so they
// fire reliably on mount — even when content mounts after the passphrase gate
// unlocks. The previous IntersectionObserver-based hook ran once and missed
// elements that mounted later, leaving them stuck at opacity 0.

const Reveal = ({ children, style, as: Tag = 'div' }) => (
  <Tag className="gsk-reveal" style={style}>{children}</Tag>
);

const Rule = () => (
  <hr style={{ border: 'none', borderTop: `1px solid ${palette.border}`, margin: '0 48px' }} />
);

const Section = ({ id, children, dark, warm, style }) => (
  <section
    id={id}
    style={{
      background: dark ? palette.darkSection : warm ? palette.warmWhite : palette.cream,
      color: dark ? palette.cream : palette.ink,
      ...style,
    }}
  >
    <div style={sectionInner}>{children}</div>
  </section>
);

const InlineNote = ({ children, dark }) => (
  <Reveal
    style={{
      background: dark
        ? palette.darkCard
        : 'linear-gradient(135deg, rgba(250,190,192,0.32) 0%, rgba(245,215,170,0.26) 50%, rgba(181,201,168,0.22) 100%), #ffffff',
      border: dark ? `1px solid ${palette.darkBorder}` : '1px solid rgba(235,160,155,0.28)',
      borderRadius: '14px',
      padding: '28px 32px',
      marginTop: '40px',
      fontFamily: fonts.sans,
      fontSize: '15px',
      color: dark ? palette.bodyTextDark : palette.bodyText,
      fontWeight: 400,
      lineHeight: 1.65,
      boxShadow: dark ? 'none' : '0 4px 18px rgba(0,0,0,0.04)',
    }}
  >
    {children}
  </Reveal>
);

const Nav = () => (
  <nav
    style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      zIndex: 100,
      background: 'rgba(250,248,244,0.94)',
      backdropFilter: 'blur(12px)',
      WebkitBackdropFilter: 'blur(12px)',
      borderBottom: `1px solid ${palette.border}`,
      padding: '0 48px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      height: '58px',
    }}
    className="gsk-nav"
  >
    <div
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        lineHeight: 1.1,
        fontFamily: fonts.serif,
        fontSize: '18px',
        fontWeight: 700,
        color: palette.ink,
        letterSpacing: '-0.2px',
      }}
    >
      <span>Real</span>
      <span style={{ position: 'relative', display: 'inline-block', marginLeft: '6px' }}>
        <span
          aria-hidden="true"
          style={{
            position: 'absolute',
            left: '-2%',
            right: '-8%',
            bottom: 0,
            height: '50%',
            background:
              'linear-gradient(90deg, rgba(235,160,155,0.85) 0%, rgba(245,185,165,0.8) 40%, rgba(250,200,160,0.75) 70%, rgba(245,215,170,0.6) 100%)',
            transform: 'skewX(-2deg) rotate(-1deg)',
            zIndex: 0,
            borderRadius: '3px 6px 5px 3px',
          }}
        />
        <span style={{ position: 'relative', zIndex: 1 }}>Patients</span>
      </span>
      <sup style={{ fontSize: '10px', fontFamily: fonts.sans, fontWeight: 400, marginLeft: '2px' }}>
        ™
      </sup>
    </div>
    <ul
      style={{ display: 'flex', gap: '22px', listStyle: 'none', alignItems: 'center', margin: 0, padding: 0 }}
      className="gsk-nav-links"
    >
      {[
        ['#hero', 'Intro'],
        ['#evolution', 'Our Evolution'],
        ['#community', 'Community'],
        ['#methods', 'Methods'],
        ['#intelligence', 'Intelligence'],
        ['#ai', 'AI & Tech'],
        ['#scope', 'GSK Fit'],
      ].map(([href, label]) => (
        <li key={href}>
          <a
            href={href}
            style={{
              fontFamily: fonts.sans,
              fontSize: '11px',
              fontWeight: 500,
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
              color: palette.muted,
              textDecoration: 'none',
              transition: 'color 0.2s',
            }}
          >
            {label}
          </a>
        </li>
      ))}
      <li>
        <span
          style={{
            fontFamily: fonts.sans,
            fontSize: '10px',
            fontWeight: 500,
            letterSpacing: '0.06em',
            textTransform: 'uppercase',
            color: palette.rust,
            border: `1px solid ${palette.rust}`,
            padding: '4px 10px',
            borderRadius: '2px',
          }}
        >
          GSK · May 2026
        </span>
      </li>
    </ul>
  </nav>
);

const Hero = () => (
  <div id="hero" style={{ background: palette.cream }}>
    <div style={{ maxWidth: '900px', margin: '0 auto', padding: '148px 48px 100px', textAlign: 'center' }}>
      <Reveal
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '10px',
          background: palette.warmWhite,
          border: `1px solid ${palette.border}`,
          padding: '7px 16px',
          borderRadius: '2px',
          marginBottom: '40px',
        }}
      >
        <span
          style={{
            width: '6px',
            height: '6px',
            background: palette.rust,
            borderRadius: '50%',
            flexShrink: 0,
          }}
        />
        <span style={{ fontFamily: fonts.sans, fontSize: '12px', color: palette.muted, letterSpacing: '0.03em' }}>
          Prepared for{' '}
          <strong style={{ color: palette.ink, fontWeight: 500 }}>GSK Patient Focused Development</strong> ·
          Capabilities Presentation · May 2026
        </span>
      </Reveal>

      <Reveal as="h1" style={h1Style}>
        Patient engagement<br />is evolving.
        <br />
        <span style={italicStyle}>We've spent ten years<br /><Highlight>leading</Highlight> the way.</span>
      </Reveal>

      <Reveal style={{ ...leadStyle, maxWidth: '620px', marginTop: '32px', marginLeft: 'auto', marginRight: 'auto' }}>
        For a decade, Real Patients has been changing how pharma connects with patient communities — building the
        relationships, advancing the methods, and developing the technology that makes next-level patient intelligence
        possible today.
      </Reveal>
    </div>
  </div>
);

const PillarRow = ({ items }) => (
  <Reveal
    style={{
      display: 'grid',
      gridTemplateColumns: 'repeat(3, 1fr)',
      gap: '1px',
      background: palette.border,
      border: `1px solid ${palette.border}`,
      marginTop: '48px',
    }}
    className="gsk-grid-3"
  >
    {items.map((it) => (
      <div key={it.name} style={{ background: palette.cardBg, padding: '34px 26px' }}>
        <span
          style={{
            fontFamily: fonts.mono,
            fontSize: '10px',
            fontWeight: 500,
            letterSpacing: '0.14em',
            textTransform: 'uppercase',
            color: palette.rust,
            marginBottom: '10px',
            display: 'block',
          }}
        >
          {it.name}
        </span>
        <h3 style={{ fontFamily: fonts.serif, fontSize: '20px', fontWeight: 700, marginBottom: '10px', margin: '0 0 10px' }}>
          {it.title}
        </h3>
        <p style={{ fontFamily: fonts.sans, fontSize: '13px', color: palette.muted, lineHeight: 1.6, fontWeight: 300, margin: 0 }}>
          {it.body}
        </p>
      </div>
    ))}
  </Reveal>
);

const WhoWeAre = () => (
  <Section id="whoweare">
    <Reveal as="span" style={labelStyle}>Who We Are</Reveal>
    <Reveal as="h2" style={h2Style}>
      Founded by patients.
      <br />
      <span style={italicStyle}>Built by researchers.</span>
    </Reveal>
    <Reveal as="p" style={leadStyle}>
      Most patient engagement vendors are one thing: a panel, a platform, or a research firm. Real Patients is all
      three — and that integration is what makes us fast, rigorous, and genuinely different.
    </Reveal>

    <PillarRow
      items={[
        {
          name: 'Community',
          title: 'A living network',
          body: 'An active, growing community across 2,500+ conditions — stewarded carefully so patients stay engaged, come back, and feel that their voice matters.',
        },
        {
          name: 'Platform',
          title: 'Built for pharma',
          body: 'A compliant, purpose-built research and intelligence platform designed with our pharma clients in mind — from compliant workflows to longitudinal data architecture.',
        },
        {
          name: 'Research',
          title: 'Pharma-grade rigor',
          body: 'Every engagement is designed and delivered by our in-house research team — with the methodological rigor pharma requires and the human care patients deserve.',
        },
      ]}
    />

    <Reveal style={{ marginTop: '64px', textAlign: 'center' }}>
      <img
        src="/founders.png"
        alt="Real Patients co-founders Jen Horonjeff and Ronnie Sharpe"
        style={{
          width: '100%',
          maxWidth: '440px',
          height: 'auto',
          borderRadius: '4px',
          boxShadow: '0 12px 40px rgba(26,23,20,0.12)',
          border: `1px solid ${palette.border}`,
        }}
      />
    </Reveal>

    <Reveal
      style={{
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: '48px',
        marginTop: '40px',
      }}
      className="gsk-founders"
    >
      <div>
        <span
          style={{
            fontFamily: fonts.mono,
            fontSize: '10px',
            letterSpacing: '0.12em',
            textTransform: 'uppercase',
            color: palette.rust,
            marginBottom: '6px',
            display: 'block',
          }}
        >
          Co-Founder & CEO
        </span>
        <div style={{ fontFamily: fonts.serif, fontSize: '22px', fontWeight: 700, marginBottom: '14px' }}>
          Jen Horonjeff, PhD
        </div>
        <p style={{ fontFamily: fonts.sans, fontSize: '14px', color: palette.muted2, lineHeight: 1.65, fontWeight: 300, margin: 0 }}>
          A patient herself — living with autoimmune disease since childhood and a brain tumor as an adult — Jen brought
          her lived experience into a research career as a mixed-methods qualitative researcher, human factors engineer,
          and human-centered designer. Her doctorate focused on patient-centered outcomes in clinical trial design,
          leading to her role as an FDA advisor. She founded Real Patients to close the gap she'd spent her career
          examining.
        </p>
      </div>
      <div>
        <span
          style={{
            fontFamily: fonts.mono,
            fontSize: '10px',
            letterSpacing: '0.12em',
            textTransform: 'uppercase',
            color: palette.rust,
            marginBottom: '6px',
            display: 'block',
          }}
        >
          Co-Founder & Chief Product Officer
        </span>
        <div style={{ fontFamily: fonts.serif, fontSize: '22px', fontWeight: 700, marginBottom: '14px' }}>
          Ronnie Sharpe
        </div>
        <p style={{ fontFamily: fonts.sans, fontSize: '14px', color: palette.muted2, lineHeight: 1.65, fontWeight: 300, margin: 0 }}>
          A cystic fibrosis patient, Ronnie founded Cystic Life — one of the first rare disease social networks —
          capturing patient insights at scale before "patient engagement" was a priority for pharma. That work made him
          a sought-after consultant to pharmaceutical companies seeking to understand hard-to-reach patient populations.
          He brings that same understanding to everything Real Patients builds.
        </p>
      </div>
    </Reveal>
  </Section>
);

const TIMELINE = [
  ['2016', 'Founded', 'Expanding access to real patients', 'We launched as Savvy Cooperative, and made it easy for organizations to engage real, diverse patients for insight and testing — removing barriers so there was no excuse not to listen.'],
  ['2018', '2018', 'Elevating the quality of patient research', "As demand grew, access alone wasn't enough. We expanded to conduct patient-centered research in-house — ensuring rigor, neutrality, compassion, and fit-for-purpose methods."],
  ['2020', '2020', 'Making insight continuous, not episodic', 'To help teams move faster and stay connected to patients, we introduced a subscription-based model — reducing friction and enabling ongoing learning through continuous engagement.'],
  ['2022', '2022', 'Designing for reuse and respect', 'Patients were being asked the same questions repeatedly. We began building Foundational Patient Experience — a way to capture insight once, and update and use it responsibly over time.'],
  ['2024', '2024', 'Building for AI from day one', "Legacy data wasn't fit for modern analytics. We launched our PRISM platform — designing patient experience data from the ground up to be structured, contextual, and AI-ready."],
  ['2026', 'Today — Real Patients™', 'The patient intelligence layer', "We rebranded to reflect what the work had always been about: ensuring that as AI and automation shape healthcare, real patient experience stays at the center."],
];

const TimelineItem = ({ row, index, isCurrent }) => {
  const onLeft = index % 2 === 1;
  const card = (
    <div
      style={{
        background: palette.cardBg,
        border: `1px solid ${isCurrent ? palette.teal : palette.border}`,
        padding: '24px 28px',
        gridColumn: onLeft ? 1 : 3,
      }}
    >
      <span
        style={{
          fontFamily: fonts.mono,
          fontSize: '10px',
          fontWeight: 500,
          letterSpacing: '0.12em',
          textTransform: 'uppercase',
          color: palette.teal,
          marginBottom: '8px',
          display: 'block',
        }}
      >
        {row[1]}
      </span>
      <h3
        style={{
          fontFamily: fonts.serif,
          fontSize: '17px',
          fontWeight: 700,
          marginBottom: '8px',
          margin: '0 0 8px',
          color: isCurrent ? palette.teal : palette.ink,
        }}
      >
        {row[2]}
      </h3>
      <p style={{ fontFamily: fonts.sans, fontSize: '13px', color: palette.muted, lineHeight: 1.55, fontWeight: 300, margin: 0 }}>
        {row[3]}
      </p>
    </div>
  );

  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: '1fr 64px 1fr',
        alignItems: 'start',
        marginBottom: '52px',
        position: 'relative',
      }}
      className="gsk-timeline-item"
    >
      {!onLeft && <div style={{ gridColumn: 1 }} />}
      {onLeft && card}
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          paddingTop: '22px',
          gap: '8px',
        }}
      >
        <div
          style={{
            width: '12px',
            height: '12px',
            borderRadius: '50%',
            background: isCurrent ? palette.ink : palette.teal,
            border: `2px solid ${palette.cream}`,
            boxShadow: `0 0 0 2px ${isCurrent ? palette.ink : palette.teal}`,
            flexShrink: 0,
          }}
        />
        <div
          style={{
            fontFamily: fonts.serif,
            fontWeight: 900,
            fontSize: '26px',
            color: isCurrent ? palette.ink : palette.teal,
            letterSpacing: '-1px',
            lineHeight: 1,
          }}
        >
          {row[0]}
        </div>
      </div>
      {!onLeft && card}
      {onLeft && <div style={{ gridColumn: 3 }} />}
    </div>
  );
};

const Evolution = () => (
  <Section id="evolution">
    <Reveal as="span" style={labelStyle}>How We Got Here</Reveal>
    <Reveal as="h2" style={h2Style}>
      Ten years of building
      <br />
      <span style={italicStyle}>toward what <Highlight>healthcare needs</Highlight> now.</span>
    </Reveal>
    <Reveal as="p" style={leadStyle}>
      We didn't arrive at patient intelligence by accident. Every shift in how we work reflects something we saw the
      industry needed before those in the field knew how to ask for it.
    </Reveal>

    <Reveal style={{ marginTop: '64px', position: 'relative', paddingBottom: '8px' }} className="gsk-timeline">
      <div
        style={{
          position: 'absolute',
          left: '50%',
          top: 0,
          bottom: 0,
          width: '2px',
          background: palette.border,
          transform: 'translateX(-50%)',
        }}
        className="gsk-timeline-track"
      />
      {TIMELINE.map((row, i) => (
        <TimelineItem key={row[0]} row={row} index={i} isCurrent={i === TIMELINE.length - 1} />
      ))}
    </Reveal>
  </Section>
);

const Community = () => (
  <Section id="community" warm>
    <Reveal as="span" style={labelStyle}>It All Starts With the Community</Reveal>
    <Reveal as="h2" style={h2Style}>
      Not a panel.
      <br />
      <span style={italicStyle}>A <Highlight>living</Highlight> network.</span>
    </Reveal>
    <Reveal as="p" style={leadStyle}>
      Most patient engagement vendors maintain a static panel they tap into on demand. We built something different — an
      active, growing community of verified patients who are engaged because they trust us, not because they were
      recruited into a database.
    </Reveal>

    <Reveal
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(3, 1fr)',
        gap: '1px',
        background: palette.border,
        border: `1px solid ${palette.border}`,
        marginTop: '48px',
      }}
      className="gsk-grid-3"
    >
      {[
        ['2,500+', 'Conditions represented', 'From the most common chronic conditions to the rarest diseases — our community spans the breadth of the patient experience, across geographies and demographics.'],
        ['Peer-to-peer', 'How patients find us', 'We grow through trusted referrals — patients learning about opportunities from others in their communities. Online patient groups, disease-specific networks, neighbors, caregivers. Real connections, not cold outreach.'],
        ['Verified', 'Identity & diagnosis confirmed', 'Every community member has identity and diagnosis confirmed through a multi-factor process before they participate in any research. No proxies. No inferences. Confirmed.'],
      ].map(([stat, title, body]) => (
        <div key={title} style={{ background: palette.cardBg, padding: '30px 24px' }}>
          <span
            style={{
              fontFamily: fonts.serif,
              fontSize: '36px',
              fontWeight: 900,
              color: palette.teal,
              letterSpacing: '-1px',
              display: 'block',
              marginBottom: '6px',
            }}
          >
            {stat}
          </span>
          <h3 style={{ fontFamily: fonts.serif, fontSize: '18px', fontWeight: 700, marginBottom: '8px', margin: '0 0 8px' }}>
            {title}
          </h3>
          <p style={{ fontFamily: fonts.sans, fontSize: '13px', color: palette.muted, lineHeight: 1.6, fontWeight: 300, margin: 0 }}>
            {body}
          </p>
        </div>
      ))}
    </Reveal>

    <InlineNote>
      <strong style={{ fontWeight: 700, color: palette.ink }}>Why this matters for you:</strong> When you need
      patients, we're not starting from scratch — we're going back to a community that already knows us and wants to
      contribute. That's what makes speed, diversity, and depth possible at the same time.
    </InlineNote>
  </Section>
);

const OpIcon = ({ name }) => {
  const stroke = palette.rust;
  const common = {
    width: 28,
    height: 28,
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke,
    strokeWidth: 1.6,
    strokeLinecap: 'round',
    strokeLinejoin: 'round',
    'aria-hidden': true,
  };
  const wrap = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '44px',
    height: '44px',
    borderRadius: '50%',
    background: 'rgba(235,160,155,0.14)',
  };
  const paths = {
    recruit: (
      <>
        <circle cx="9" cy="8" r="3" />
        <circle cx="17" cy="10" r="2.4" />
        <path d="M3 19c1-3 3.5-4.5 6-4.5s5 1.5 6 4.5" />
        <path d="M15 17c.7-1.8 2.5-2.7 4-2.7 1.3 0 2.7.7 3 2" />
      </>
    ),
    verify: (
      <>
        <path d="M12 3l8 3v5c0 4.5-3 8.2-8 10-5-1.8-8-5.5-8-10V6l8-3z" />
        <path d="M9 12l2 2 4-4" />
      </>
    ),
    consent: (
      <>
        <path d="M6 3h9l4 4v14H6z" />
        <path d="M14 3v5h5" />
        <path d="M9 13l2 2 4-4" />
        <path d="M9 18h7" />
      </>
    ),
    schedule: (
      <>
        <rect x="3.5" y="5" width="17" height="15" rx="2" />
        <path d="M3.5 10h17" />
        <path d="M8 3v4M16 3v4" />
        <circle cx="12" cy="15" r="1.6" fill={stroke} stroke="none" />
      </>
    ),
    compensate: (
      <>
        <rect x="3" y="6" width="18" height="12" rx="2" />
        <path d="M3 10h18" />
        <circle cx="8" cy="14.5" r="1.4" />
        <path d="M14 14.5h4" />
      </>
    ),
    manage: (
      <>
        <rect x="6" y="4" width="12" height="17" rx="1.5" />
        <path d="M9 4h6v3H9z" />
        <path d="M9 11l1.5 1.5L13 10" />
        <path d="M9 16h7" />
      </>
    ),
  };
  return (
    <span style={wrap}>
      <svg {...common}>{paths[name]}</svg>
    </span>
  );
};

const Operations = () => (
  <Section id="operations">
    <Reveal as="span" style={labelStyle}>The Work Behind Every Engagement</Reveal>
    <Reveal as="h2" style={h2Style}>
      Everything that happens between
      <br />
      <span style={italicStyle}>a research question and the report.</span>
    </Reveal>
    <Reveal as="p" style={leadStyle}>
      Before a single insight reaches your team, there's an entire operational layer that has to be done well — and
      that most vendors underestimate or outsource. We own all of it.
    </Reveal>

    <Reveal
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(3, 1fr)',
        gap: '1px',
        background: palette.border,
        border: `1px solid ${palette.border}`,
        marginTop: '48px',
      }}
      className="gsk-grid-3"
    >
      {[
        ['recruit', 'Recruitment', 'We source and recruit verified patients matched to your exact target profile — by condition, treatment history, care pathway, geography, demographics, and other criteria.'],
        ['verify', 'Verification', 'Identity and diagnosis confirmed through a multi-factor process before any patient participates — maintaining the integrity of the insights you receive.'],
        ['consent', 'Consenting', 'Informed consent managed compliantly for every engagement — tailored to the research context and appropriate for the patient population.'],
        ['schedule', 'Scheduling', 'Participant scheduling coordinated across time zones, care routines, and availability — participants receive reminders and support the whole way through.'],
        ['compensate', 'Compensation', 'Fair, compliant participant compensation managed end-to-end. Patients are paid appropriately for their time and expertise — with all compliance requirements met.'],
        ['manage', 'Project Management', 'Timeline management, stakeholder coordination, protocol alignment, and quality control — from kick-off to final report. One point of contact for your team throughout.'],
      ].map(([icon, title, body]) => (
        <div key={title} style={{ background: palette.cardBg, padding: '28px 24px' }}>
          <OpIcon name={icon} />
          <h3 style={{ fontFamily: fonts.serif, fontSize: '17px', fontWeight: 700, marginBottom: '6px', margin: '14px 0 6px' }}>
            {title}
          </h3>
          <p style={{ fontFamily: fonts.sans, fontSize: '13px', color: palette.muted, lineHeight: 1.55, fontWeight: 300, margin: 0 }}>
            {body}
          </p>
        </div>
      ))}
    </Reveal>
  </Section>
);

const Patients = () => (
  <Section id="patients">
    <Reveal as="span" style={labelStyle}>Better for Patients Too</Reveal>
    <Reveal as="h2" style={h2Style}>
      The model that works
      <br />
      <span style={italicStyle}>for the <Highlight>patients</Highlight> you serve.</span>
    </Reveal>
    <Reveal as="p" style={leadStyle}>
      Patient engagement that only works for pharma isn't sustainable. The programmatic approach we've built isn't just
      more useful for your teams — it's meaningfully better for the patients who make it possible.
    </Reveal>

    <Reveal
      style={{
        marginTop: '56px',
        background: `linear-gradient(135deg, rgba(250,190,192,0.32) 0%, rgba(245,215,170,0.26) 50%, rgba(181,201,168,0.22) 100%), ${palette.cardBg}`,
        border: '1px solid rgba(235,160,155,0.28)',
        borderRadius: '14px',
        padding: '44px 48px',
        boxShadow: '0 8px 32px rgba(0,0,0,0.05)',
        textAlign: 'center',
      }}
      className="gsk-patient-callout"
    >
      <div
        style={{
          fontFamily: fonts.sans,
          fontSize: '11px',
          fontWeight: 700,
          letterSpacing: '0.18em',
          textTransform: 'uppercase',
          color: palette.rust,
          marginBottom: '18px',
        }}
      >
        What patients tell us about traditional research models
      </div>
      <blockquote
        style={{
          fontFamily: fonts.serif,
          fontSize: 'clamp(24px, 3vw, 32px)',
          fontStyle: 'italic',
          fontWeight: 500,
          color: palette.ink,
          lineHeight: 1.3,
          margin: 0,
          letterSpacing: '-0.3px',
        }}
      >
        "I applied, but I never get picked."
      </blockquote>
    </Reveal>

    <Reveal as="p" style={{ ...leadStyle, marginTop: '32px', fontSize: '16px' }}>
      This is what patients tell us about traditional advisory boards and research recruitment. Hundreds apply for five
      to ten spots — and the rest are left feeling invisible. Their experience goes uncaptured. Their willingness to
      contribute goes wasted.
    </Reveal>

    <Reveal
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(3, 1fr)',
        gap: '1px',
        background: palette.border,
        border: `1px solid ${palette.border}`,
        marginTop: '40px',
      }}
      className="gsk-grid-3"
    >
      {[
        ['Heard regularly, not just recruited', "The intelligence model means patients contribute on an ongoing basis — not only when they're lucky enough to be selected for a single project. Their voice enters a living system that actually uses what they share."],
        ['Experiences that compound into meaning', 'In traditional research, a patient shares their story once and it disappears into a report. In our model, each contribution shapes decisions across the product lifecycle. Their experience matters — and they can feel it.'],
        ['A community, not a panel', "We've spent a decade building trust with patient communities — not just recruiting from them. That distinction is why patients respond when we ask, and why the intelligence we generate is grounded in relationships."],
      ].map(([title, body]) => (
        <div key={title} style={{ background: palette.cardBg, padding: '30px' }}>
          <h3 style={{ fontFamily: fonts.serif, fontSize: '19px', fontWeight: 700, marginBottom: '8px', margin: '0 0 8px' }}>
            {title}
          </h3>
          <p style={{ fontFamily: fonts.sans, fontSize: '13px', color: palette.muted, lineHeight: 1.6, fontWeight: 300, margin: 0 }}>
            {body}
          </p>
        </div>
      ))}
    </Reveal>
  </Section>
);

const AITech = () => (
  <Section id="ai">
    <Reveal as="span" style={labelStyle}>AI & Technology</Reveal>
    <Reveal as="h2" style={h2Style}>
      The technology is only as good
      <br />
      <span style={italicStyle}>as the data and systems behind it.</span>
    </Reveal>
    <Reveal as="p" style={leadStyle}>
      Everyone wants to leverage the latest technology. Our position is that AI is only as powerful as the data and
      systems it's built on — which is why we've been deliberate, from day one, about how we structure, store, and use
      patient experience data. Both because we work with patients, and because we work with pharma.
    </Reveal>

    <Reveal
      style={{
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: '1px',
        background: palette.border,
        border: `1px solid ${palette.border}`,
        marginTop: '48px',
      }}
      className="gsk-grid-2"
    >
      {[
        ['Research Intake', 'AI Research Assistant', 'Our AI Research Assistant engages your team in a live, conversational intake to transform a vague question into a clear, answerable patient inquiry — a single conversation can replace weeks of meetings and scoping.'],
        ['Intelligence Platform', 'PRISM', 'Our Patient Research Intelligence System provides a queryable, longitudinal intelligence base — structured, contextual, and AI-ready from day one. Every engagement enriches it. Every question gets easier to answer.'],
        ['Community Matching', 'Intelligent Targeting', 'AI-assisted matching identifies the right verified patients for each inquiry — by condition, treatment stage, care pathway, geography, and engagement history. The match is the AI; the relationship is built with real patients.'],
        ['Analysis', 'Synthesis at Scale', 'AI-assisted thematic coding speeds the first pass. A researcher reviews and refines every theme before it leaves the building — so the insight you get is fast and defensible.'],
      ].map(([mode, title, body]) => (
        <div key={title} style={{ background: palette.cardBg, padding: '30px' }}>
          <span
            style={{
              fontFamily: fonts.mono,
              fontSize: '10px',
              fontWeight: 500,
              letterSpacing: '0.14em',
              textTransform: 'uppercase',
              color: palette.rust,
              marginBottom: '8px',
              display: 'block',
            }}
          >
            {mode}
          </span>
          <h3 style={{ fontFamily: fonts.serif, fontSize: '19px', fontWeight: 700, marginBottom: '8px', margin: '0 0 8px' }}>
            {title}
          </h3>
          <p style={{ fontFamily: fonts.sans, fontSize: '13px', color: palette.muted, lineHeight: 1.6, fontWeight: 300, margin: 0 }}>
            {body}
          </p>
        </div>
      ))}
    </Reveal>

    <InlineNote>
      <strong style={{ fontWeight: 500, color: palette.ink }}>
        The verified patient relationship — built over years of trust — is something AI alone can never replicate.
      </strong>{' '}
      We use technology to get you to real patients faster and to make their contributions more meaningful. Not to
      simulate them. Everything here is built in partnership with real patients.
    </InlineNote>
  </Section>
);

const Scope = () => (
  <Section id="scope">
    <Reveal as="span" style={labelStyle}>GSK RFI Scope Alignment</Reveal>
    <Reveal as="h2" style={h2Style}>
      Where our core capabilities
      <br />
      <span style={italicStyle}>are strongest.</span>
    </Reveal>
    <Reveal as="p" style={leadStyle}>
      GSK's RFI identified ten activity areas. Here's an honest map across all of them — what we're built for, where we
      can support, and where another partner is the better fit.
    </Reveal>

    <Reveal style={{ marginTop: '48px', overflowX: 'auto' }} className="gsk-scope-wrap">
      <table
        style={{
          width: '100%',
          borderCollapse: 'collapse',
          fontFamily: fonts.sans,
        }}
      >
        <thead>
          <tr>
            <th style={scopeThStyle}>GSK Activity Area</th>
            <th style={scopeThStyle}>Our Capability</th>
            <th style={scopeThStyle}>Key Differentiator</th>
          </tr>
        </thead>
        <tbody>
          {[
            ['yes', 'Patient Sourcing', 'End-to-end recruitment, verification, consent, and compensation — matched to your exact patient profile.', 'Peer-to-peer outreach through trusted communities. More diversity, real engagement, patients who show up.'],
            ['yes', 'Patient Panel / Survey / Interviews', 'Global surveys and IDIs and beyond, delivered end-to-end. Rigor fit for publication and regulatory submission.', 'Fit-for-purpose methods, including Activity Boards and Pulse Checks™ that go beyond traditional methods.'],
            ['yes', 'Patient Journey Work', 'Full patient journey mapping — symptoms through chronic management — grounded in verified patient voice.', "Longitudinal data sets that evolve with the patient experience — not a single snapshot."],
            ['yes', 'E2E Advisory Boards', 'Sourcing, verification, scheduling, facilitation, and reporting — virtual or in person.', 'On-demand patient intelligence and Pulse Check™ pre-work means you walk into the room knowing the key themes already.'],
            ['yes', 'Clinical Trial Design & Support', 'Patient feedback on protocol design, study materials, ICF, burden, and unmet needs — via IDIs, Activity Boards, and Protocol Review.', "Proven In-Depth Protocol Review methodology. We fill the patient-voice gaps literature can't."],
            ['partial', 'Patient Org Mapping', 'Relationships across patient organizations; we can support mapping the landscape for a given TA.', "We know leaders in patient organizations — not just their names in a directory."],
            ['partial', 'Patient Journey — Literature Review', 'We complement literature reviews with primary patient voice.', "What literature can't capture: the gaps between what's documented and what patients live."],
            ['partial', 'Lay Person Summaries', "We don't write them, but we test them with verified patients and return structured feedback.", "Feedback from people who actually have the condition — not a generic readability panel."],
            ['partial', 'Patient Ambassador Programs / Video', 'We identify and engage patients for ambassador and video programs. Production handled by partners.', 'Our network surfaces patients already active in their communities — natural ambassadors.'],
            ['no', 'Live Event / Production Management', 'Stage and event production isn\'t our wheelhouse. We partner with specialist firms.', 'Knowing where we stop is part of how we work. We bring the patients; partners help execute.'],
          ].map(([level, area, capability, diff]) => (
            <tr key={area}>
              <td style={scopeTdFirstStyle}>
                <Badge level={level} />
                <br />
                {area}
              </td>
              <td style={scopeTdMidStyle}>{capability}</td>
              <td style={scopeTdHiStyle}>{diff}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </Reveal>
  </Section>
);

const Badge = ({ level }) => {
  const styles = {
    yes: { bg: 'rgba(107,154,122,0.10)', color: '#3F7556', border: '#6B9A7A', label: 'Core Strength' },
    partial: { bg: 'rgba(201,162,39,0.08)', color: '#A0801F', border: palette.gold, label: 'Can Support' },
    no: { bg: 'transparent', color: palette.muted, border: palette.muted, label: 'Out of Scope' },
  }[level];
  return (
    <span
      style={{
        display: 'inline-block',
        fontFamily: fonts.mono,
        fontSize: '10px',
        fontWeight: 600,
        letterSpacing: '0.12em',
        textTransform: 'uppercase',
        padding: '3px 8px',
        marginBottom: '6px',
        background: styles.bg,
        color: styles.color,
        border: `1px solid ${styles.border}`,
      }}
    >
      {styles.label}
    </span>
  );
};

const scopeThStyle = {
  fontFamily: fonts.mono,
  fontSize: '10px',
  letterSpacing: '0.12em',
  textTransform: 'uppercase',
  color: palette.muted,
  textAlign: 'left',
  padding: '10px 16px',
  borderBottom: `2px solid ${palette.border}`,
};
const scopeTdFirstStyle = {
  padding: '16px',
  borderBottom: `1px solid ${palette.border}`,
  fontSize: '13px',
  verticalAlign: 'top',
  fontWeight: 400,
  color: palette.ink,
  width: '28%',
};
const scopeTdMidStyle = {
  padding: '16px',
  borderBottom: `1px solid ${palette.border}`,
  fontSize: '13px',
  verticalAlign: 'top',
  color: palette.muted,
  fontWeight: 300,
  lineHeight: 1.6,
  width: '40%',
};
const scopeTdHiStyle = {
  padding: '16px',
  borderBottom: `1px solid ${palette.border}`,
  fontSize: '13px',
  verticalAlign: 'top',
  color: palette.ink,
  fontWeight: 500,
  lineHeight: 1.6,
  width: '32%',
  borderLeft: `1px solid ${palette.border}`,
  background: palette.hiBg,
};

const Pipeline = () => (
  <Section id="pipeline" warm>
    <Reveal as="span" style={labelStyle}>GSK Pipeline Coverage</Reveal>
    <Reveal as="h2" style={h2Style}>
      We already have community
      <br />
      <span style={italicStyle}>across your pipeline.</span>
    </Reveal>
    <Reveal as="p" style={leadStyle}>
      Across GSK's active pipeline, Real Patients already has verified community members ready to engage — from Phase I
      assets to near-launch programs.
    </Reveal>

    <Reveal style={{ marginTop: '40px', overflowX: 'auto' }} className="gsk-pipeline-wrap">
      <table
        style={{
          width: '100%',
          borderCollapse: 'collapse',
          fontFamily: fonts.sans,
          fontSize: '13px',
        }}
      >
        <thead>
          <tr>
            <th style={scopeThStyle}>Asset</th>
            <th style={scopeThStyle}>Indication</th>
            <th style={scopeThStyle}>Stage</th>
            <th style={{ ...scopeThStyle, textAlign: 'center' }}>RP Community</th>
          </tr>
        </thead>
        <tbody>
          {[
            ['Depemokimab (Exdensur)', 'CRSwNP, EGPA, HES, COPD', 'Registration / Phase III'],
            ['Bepirovirsen', 'Chronic Hep B, HIV/HBV', 'Registration / Phase II'],
            ['Belantamab mafodotin (Blenrep)', 'Multiple myeloma, AL amyloidosis', 'Phase III / Phase II'],
            ['Dostarlimab (Jemperli)', 'Colon cancer, H&N squamous, endometrial', 'Phase III'],
            ['TSLP antibody', 'Asthma', 'Phase II'],
            ['Ozureprubart', 'Food allergies', 'Phase II'],
            ['GSK4527363 / GSK4347859', 'Systemic lupus erythematosus', 'Phase I'],
            ['Efimosfermin alfa', 'MASH', 'Phase III'],
            ['Tebipenem pivoxil', 'Complicated UTI', 'Registration'],
            ['Nivisnebart', "Alzheimer's", 'Phase II'],
          ].map(([asset, indication, stage]) => (
            <tr key={asset}>
              <td
                style={{
                  padding: '13px 14px',
                  borderBottom: `1px solid ${palette.border}`,
                  color: palette.ink,
                  fontWeight: 500,
                  fontStyle: 'italic',
                  verticalAlign: 'middle',
                }}
              >
                {asset}
              </td>
              <td
                style={{
                  padding: '13px 14px',
                  borderBottom: `1px solid ${palette.border}`,
                  color: palette.muted2,
                  verticalAlign: 'middle',
                }}
              >
                {indication}
              </td>
              <td
                style={{
                  padding: '13px 14px',
                  borderBottom: `1px solid ${palette.border}`,
                  color: palette.muted2,
                  verticalAlign: 'middle',
                }}
              >
                {stage}
              </td>
              <td
                style={{
                  padding: '13px 14px',
                  borderBottom: `1px solid ${palette.border}`,
                  textAlign: 'center',
                  verticalAlign: 'middle',
                }}
              >
                <span
                  aria-label="Community available"
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '26px',
                    height: '26px',
                    borderRadius: '50%',
                    background: 'rgba(107,154,122,0.14)',
                    color: '#3F7556',
                    fontWeight: 700,
                    fontSize: '14px',
                    lineHeight: 1,
                  }}
                >
                  ✓
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </Reveal>

    <InlineNote>
      We already have verified community members across each therapeutic area shown — built through years of
      peer-to-peer outreach, not assembled for this conversation. When you're ready to engage, the relationships are
      already in place.
    </InlineNote>
    <p
      style={{
        fontFamily: fonts.sans,
        fontSize: '11px',
        color: palette.muted,
        marginTop: '14px',
        textAlign: 'center',
        fontStyle: 'italic',
      }}
    >
      Pipeline assets shown are publicly disclosed in GSK's R&amp;D pipeline and the U.S. ClinicalTrials.gov registry.
    </p>
  </Section>
);

const CTA = () => (
  <section
    id="contact"
    style={{
      background:
        'linear-gradient(135deg, rgba(250,190,192,0.45) 0%, rgba(245,215,170,0.4) 50%, rgba(181,201,168,0.35) 100%), #FAF8F6',
      color: palette.ink,
      textAlign: 'center',
      padding: '120px 48px',
      position: 'relative',
      overflow: 'hidden',
    }}
  >
    <div
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        lineHeight: 1.1,
        fontFamily: fonts.serif,
        fontSize: '20px',
        fontWeight: 700,
        color: palette.ink,
        letterSpacing: '-0.2px',
        marginBottom: '40px',
      }}
    >
      <span>Real</span>
      <span style={{ position: 'relative', display: 'inline-block', marginLeft: '7px' }}>
        <span
          aria-hidden="true"
          style={{
            position: 'absolute',
            left: '-2%',
            right: '-8%',
            bottom: 0,
            height: '50%',
            background:
              'linear-gradient(90deg, rgba(235,160,155,0.95) 0%, rgba(245,185,165,0.9) 40%, rgba(250,200,160,0.85) 70%, rgba(245,215,170,0.7) 100%)',
            transform: 'skewX(-2deg) rotate(-1deg)',
            zIndex: 0,
            borderRadius: '3px 6px 5px 3px',
          }}
        />
        <span style={{ position: 'relative', zIndex: 1 }}>Patients</span>
      </span>
      <sup style={{ fontSize: '11px', fontFamily: fonts.sans, fontWeight: 400, marginLeft: '2px' }}>™</sup>
    </div>

    <h2
      style={{
        fontFamily: fonts.serif,
        fontWeight: 700,
        fontSize: 'clamp(38px, 5vw, 60px)',
        color: palette.ink,
        lineHeight: 1.12,
        letterSpacing: '-0.5px',
        margin: 0,
        maxWidth: '780px',
        marginLeft: 'auto',
        marginRight: 'auto',
      }}
    >
      Let's keep building
      <br />
      <span style={italicStyle}>something real together.</span>
    </h2>
    <p
      style={{
        fontFamily: fonts.sans,
        color: palette.bodyText,
        marginTop: '24px',
        fontSize: '17px',
        fontWeight: 400,
        maxWidth: '560px',
        marginLeft: 'auto',
        marginRight: 'auto',
        lineHeight: 1.7,
      }}
    >
      Tell us where the next question is. We'll bring verified patients, the right method, and a starting point that
      builds on everything we've learned together.
    </p>
    <a
      href="mailto:sbd@realpatients.com?subject=GSK%20%C3%97%20Real%20Patients"
      style={{
        display: 'inline-block',
        marginTop: '48px',
        background: 'linear-gradient(135deg, #EBA09B 0%, #F5B9A5 100%)',
        color: '#fff',
        fontFamily: fonts.sans,
        fontSize: '15px',
        fontWeight: 600,
        letterSpacing: '0.02em',
        padding: '16px 32px',
        textDecoration: 'none',
        borderRadius: '8px',
        boxShadow: '0 6px 20px rgba(235,160,155,0.35)',
        transition: 'transform 0.2s, box-shadow 0.2s',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = 'translateY(-2px)';
        e.currentTarget.style.boxShadow = '0 10px 28px rgba(235,160,155,0.45)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = 'translateY(0)';
        e.currentTarget.style.boxShadow = '0 6px 20px rgba(235,160,155,0.35)';
      }}
    >
      Start the conversation →
    </a>
  </section>
);

const PageFooter = () => (
  <footer
    style={{
      background: palette.darkSection,
      color: palette.muted,
      padding: '40px 48px',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      fontFamily: fonts.sans,
      fontSize: '12px',
      flexWrap: 'wrap',
      gap: '16px',
    }}
  >
    <div>
      <div style={{ fontFamily: fonts.serif, fontWeight: 700, fontSize: '16px', color: palette.cream }}>
        Real Patients™
      </div>
      <div style={{ marginTop: '6px', fontSize: '11px', color: '#5a5248' }}>
        Formerly Savvy Cooperative · Est. 2016 · Made with care for patients, by patients.
      </div>
    </div>
    <div style={{ textAlign: 'right' }}>
      <div>sbd@realpatients.com</div>
      <div style={{ marginTop: '4px' }}>© 2026 Real Patients</div>
    </div>
  </footer>
);

const Styles = () => (
  <style>{`
    @keyframes gsk-fade-in {
      from { opacity: 0; transform: translateY(8px); }
      to   { opacity: 1; transform: translateY(0); }
    }
    .gsk-reveal { animation: gsk-fade-in 0.4s ease 0.05s both; }
    .gsk-reveal.gsk-visible { opacity: 1; transform: translateY(0); }
    @media (prefers-reduced-motion: reduce) {
      .gsk-reveal { animation: none; opacity: 1; transform: none; }
    }
    .gsk-page { background: ${palette.cream}; min-height: 100vh; }
    .gsk-nav-links a:hover { color: ${palette.rust} !important; }
    @media (max-width: 900px) {
      .gsk-nav { padding: 0 20px !important; }
      .gsk-nav-links li:not(:last-child) { display: none; }
      .gsk-grid-3 { grid-template-columns: 1fr !important; }
      .gsk-grid-2 { grid-template-columns: 1fr !important; }
      .gsk-stat-row { grid-template-columns: 1fr !important; }
      .gsk-stat-card-grid { grid-template-columns: 1fr !important; gap: 28px !important; }
      .gsk-founders { grid-template-columns: 1fr !important; gap: 32px !important; }
      .gsk-timeline-track { display: none; }
      .gsk-timeline-item { grid-template-columns: 1fr !important; gap: 12px; }
      .gsk-timeline-item > div:nth-child(2) { display: none !important; }
    }
    @media (max-width: 768px) {
      .gsk-page section > div, .gsk-page #hero > div { padding-left: 24px !important; padding-right: 24px !important; }
      .gsk-page section > div { padding-top: 72px !important; padding-bottom: 72px !important; }
    }
  `}</style>
);

const logViewOnce = () => {
  // Reuse the patientintel webhook so view tracking lands in the same Google Sheet.
  // Apps Script writes any param with a matching column header — we send page='gsk'.
  try {
    const url = process.env.REACT_APP_PATIENTINTEL_GATE_WEBHOOK;
    if (!url) return;
    const params = new URLSearchParams({
      event: 'view',
      token: new URLSearchParams(window.location.search).get('k') || '',
      timestamp: new Date().toISOString(),
      user_agent: navigator.userAgent,
      referrer: document.referrer || '',
      page: 'gsk',
    }).toString();
    fetch(`${url}?${params}`, { method: 'GET', mode: 'no-cors' });
  } catch {
    /* swallow — view logging is best-effort */
  }
};

export const GSK = () => {
  useGoogleFonts();

  useEffect(() => {
    logViewOnce();
  }, []);

  return (
    <GSKGate>
      <div className="gsk-page" style={{ fontFamily: fonts.sans, color: palette.ink, background: palette.cream }}>
        <SEOHead
          title="Real Patients — GSK Capabilities"
          description="A capabilities presentation prepared for GSK Patient Focused Development. Six years of partnership, 17 engagements, and the patient intelligence layer built for what comes next."
          url="https://www.realpatients.com/g052026"
          noIndex={true}
        />
        <Styles />
        <Nav />
        <Hero />
        <Rule />
        <WhoWeAre />
        <Rule />
        <Evolution />
        <Rule />
        <Community />
        <Rule />
        <Operations />
        <ChallengeSection />
        <MethodsSection />
        <IntelligenceSection />
        <DemoSection />
        <Patients />
        <DifferentSection />
        <Rule />
        <AITech />
        <Rule />
        <Scope />
        <Rule />
        <Pipeline />
        <CTA />
        <PageFooter />
      </div>
    </GSKGate>
  );
};

export default GSK;
